import { Component, inject, OnInit } from '@angular/core';
import {takeUntil} from "rxjs/operators";
import {ActivatedRoute, Router} from "@angular/router";
import {CompoApp} from "../../../../services/CompoApp";
import {UnsubscribeBaseModel} from "../../../../base/model/UnsubscribeBase.model";
import {FormControl, UntypedFormGroup, Validators} from "@angular/forms";
import Constants from "../../../../Constants";
import { AuthserviceService } from "../login-page/authservice.service";
import {Title} from "@angular/platform-browser";
import {GlobalMessageClientService} from "../../../../services/client/global-message-client-service/global-message-client.service";
import {MessageDesignBUtils} from "../../../components/message-design-b/message-design-b.utils";
import {InvitationRequestService} from "../../../../services/invitation-request.service";
import { SseSingletonService } from '../../../../services/sse/sse-singleton.service';
import { PropagateOpts } from '../../../../models/form/base-cp-form.model';
import { AuthPageCommonComponent } from '../auth-page-common.component';
import { ClickOutsideDirective } from '../../../../directives/click-outside/click-outside.directive';
import { FormInputComponent } from '../../../components/input/form-input/form-input.component';
import { PasswordRequirementsComponent } from '../../../components/password-requirements/password-requirements.component';
import { ButtonComponent } from '../../../globals/button/button/button.component';
import { MessageDesignBSmallComponent } from '../../../components/message-design-b/message-design-b-small/message-design-b-small.component';
import { TranslateModule } from '@ngx-translate/core';
import { JwtAuthenticationResponse } from '../login-page/jwt-authentication-response.interface';

/**
 * RestorePasswordPageComponent used for following use case:
 * User clicked the **Forgot Password** button on the login page and requested a password restore link
 * User get the password restore email, click the link to restore the forgotten password
 */
@Component({
    selector: 'app-restore-password-page',
    templateUrl: './restore-password-page.component.html',
    styleUrls: ['./restore-password-page.component.scss', '../auth-page-common.scss'],
    imports: [AuthPageCommonComponent, ClickOutsideDirective, FormInputComponent, PasswordRequirementsComponent, ButtonComponent, MessageDesignBSmallComponent, TranslateModule]
})
export class RestorePasswordPageComponent extends UnsubscribeBaseModel implements OnInit {

  //region READONLY FIELDS
  readonly PASSWORD_MIN_LENGTH: number = 8;
  //endregion

  //region INJECTIONS
  private sseSingletonService: SseSingletonService = inject(SseSingletonService);
  private authService: AuthserviceService = inject(AuthserviceService);
  private titleService: Title = inject(Title);
  private router: Router = inject(Router);
  private app: CompoApp = inject(CompoApp);
  private invitationRequestService: InvitationRequestService = inject(InvitationRequestService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private messageClientService: GlobalMessageClientService = inject(GlobalMessageClientService);
  //endregion

  //region FORM
  readonly PASSWORD: string = 'password';
  readonly formGroup: UntypedFormGroup = new UntypedFormGroup({
    [this.PASSWORD]: new FormControl(null)
  });
  //endregion

  //region FIELDS
  private email: string;
  private uuid: string;
  isReferenceInvalid: boolean;
  showPasswordInputErrors: boolean = false;
  passwordValidatorsSet: boolean = false;
  showErrorMessage: boolean;
  inProgress: boolean;
  //endregion

  //region LIFECYCLE
  ngOnInit() {
    this.activatedRoute.params
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(param => {
        this.app.data.clear();
        const uuid: string = param['uuid'];
        this.invitationRequestService.validateInvitationUuid(uuid)
          .subscribe(resp => {
            if (!!resp.valid) {
              this.email = resp.email;
              this.uuid = uuid;
              this.showErrorMessage = false;
              this.isReferenceInvalid = false;
              this.formGroup.setControl(this.PASSWORD, new FormControl(null));
            } else {
              this.showErrorMessage = true;
              this.isReferenceInvalid = true;
              this.formGroup.setControl(this.PASSWORD, null);
              //navigate to login page in 10 Seconds
              setTimeout(() => {
                //navigate to login page
                this.router.navigate(['']);
              }, 10000);
            }
          });
      });

    this.formGroup.valueChanges
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((value: any) => {
        if (value) {
          const passwordValue: string = value[this.PASSWORD];
          if (this.showPasswordInputErrors && (!passwordValue || passwordValue.length === 0)) {
            this.showPasswordInputErrors = false;
          }
          if (!this.passwordValidatorsSet && passwordValue && passwordValue.length > 0) {
            this.setPasswordValidators();
          }
        }
      });
  }
  //endregion

  //region METHODS
  onPasswordInputLostFocus(): void {
    const password: string = this.formGroup.get(this.PASSWORD).value;
    if (password && password.length > 0 && !this.showPasswordInputErrors) {
      this.showPasswordInputErrors = true;
    }
  }

  onRestorePassword(): void {
    if (this.formGroup?.invalid || this.inProgress || this.isReferenceInvalid) {
      return;
    }

    const password: string = this.formGroup.get(this.PASSWORD).value;

    if (!password
      || !password.length
      || password.length < this.PASSWORD_MIN_LENGTH
      || !password.match(Constants.PASSWORD_REGEX)
    ) {
      this.setPasswordValidators();
      return;
    }

    this.inProgress = true;
    this.formGroup?.get(this.PASSWORD)?.disable(PropagateOpts.EMIT_EVENT_FALSE);

    this.authService.restorePwd(this.email, password, this.uuid)
      .subscribe({
        next: (result: JwtAuthenticationResponse) => {
          if (!result?.token) {
            return;
          }

          this.authService.initAccessModeFromRoleId(result.selectedUserRoleId);

          this.sseSingletonService.disconnect();
          this.sseSingletonService.connect();
          this.titleService.setTitle('Objack');
          this.app.data.setPendingUrl(null);
          this.messageClientService.messages$.next(MessageDesignBUtils.createResetPasswordSuccess());

          setTimeout(() => {
            if (result.roleCount > 1) {
              this.router.navigate(['/selectRole'], {queryParams: {roleCount: result.roleCount}});
            } else {
              this.router.navigate(['/home/prozesse/jobs']);
              this.app.on.loginTrigger.next(this.app.data.getUser());
            }
          }, 1000);
        },
        error: () => {
          this.inProgress = false;
          this.formGroup?.get(this.PASSWORD)?.enable(PropagateOpts.EMIT_EVENT_FALSE);
        }
      });
  }

  private setPasswordValidators(): void {
    const passwordControl: FormControl = this.formGroup.get(this.PASSWORD) as FormControl;
    if (passwordControl) {
      passwordControl.setValidators(
        [
          Validators.required, Validators.minLength(8),
          Validators.pattern(Constants.PASSWORD_REGEX)
        ]
      );
      passwordControl.updateValueAndValidity(PropagateOpts.EMIT_EVENT_FALSE);
      this.passwordValidatorsSet = true;
    }
  }
}
